import { AudioGuidePlaybackStatus, AudioGuideScript } from "@/types/audio-guide"
import { ClientRateLimitException } from "@/types/client-exceptions"
import { EntityRef } from "@/types/entity"
import { Place, PlacePhoto, PlaceType } from "@/types/place"
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"

import { setAnalyticsUserProperties } from "@/lib/analytics/amplitude-utils"
import { releaseAudioSession } from "@/lib/audio-session-unlock"
import { buildClientAxios } from "@/lib/client-axios"
import { OpenAIAudioGuideEngine } from "@/lib/openai-audio-guide-engine"
import { AUDIO_GUIDE_SCRIPTS, VoiceApi } from "@/lib/voice-api"
import { showRegisterGateDialog } from "./auth-thunks"
import { RootState } from "./redux-store"

const openAiPlaybackEngine = new OpenAIAudioGuideEngine()
let sessionCounter = 0

let _voiceApi: VoiceApi | null = null
function getVoiceApi(): VoiceApi {
  if (!_voiceApi) {
    _voiceApi = new VoiceApi(buildClientAxios())
  }
  return _voiceApi
}

let currentPlace: Place | null = null

export interface AudioGuidePlace {
  entity_ref: EntityRef
  name: string
  type: PlaceType
  photos: PlacePhoto[]
  canonical_url: string
}

export interface AudioGuideState {
  status: AudioGuidePlaybackStatus
  place: AudioGuidePlace | null
  activeScript: AudioGuideScript
  elapsed: number
  duration: number
  totalScripts: number
  error: string | null
  transcript: string | null
}

const initialState: AudioGuideState = {
  status: "idle",
  place: null,
  activeScript: "overview",
  elapsed: 0,
  duration: 0,
  totalScripts: AUDIO_GUIDE_SCRIPTS.length,
  error: null,
  transcript: null,
}

function toAudioGuidePlace(place: Place): AudioGuidePlace {
  return {
    entity_ref: place.entity_ref,
    name: place.name,
    type: place.type,
    photos: place.photos.length > 0 ? [place.photos[0]] : [],
    canonical_url: place.canonical_url,
  }
}

function cacheKey(
  placeRef: string,
  script: AudioGuideScript,
  voiceId: string
): string {
  return `${placeRef}:${script}:openai:${voiceId}`
}

function getNextScript(script: AudioGuideScript): AudioGuideScript | null {
  const index = AUDIO_GUIDE_SCRIPTS.indexOf(script)
  return AUDIO_GUIDE_SCRIPTS[index + 1] ?? null
}

function openAiVoiceId(state: RootState): string {
  const preference = state.userSlice.userProfile.assistant_voice
  return preference?.provider === "openai" ? preference.voice : "marin"
}

type StartAudioGuideArgs = {
  place: Place
  script?: AudioGuideScript
}

export const startAudioGuide = createAsyncThunk<
  void,
  StartAudioGuideArgs,
  { state: RootState }
>(
  "audioGuide/startAudioGuide",
  async ({ place, script = "overview" }, thunkApi) => {
    const { dispatch, getState } = thunkApi
    const session = ++sessionCounter
    const state = getState()
    const voiceId = openAiVoiceId(state)
    const key = cacheKey(place.entity_ref, script, voiceId)
    const isStale = () => session !== sessionCounter

    currentPlace = place
    dispatch(
      audioGuideSlice.actions.setSession({
        place: toAudioGuidePlace(place),
        script,
      })
    )
    dispatch(audioGuideSlice.actions.setStatus("loading"))
    dispatch(audioGuideSlice.actions.setProgress({ elapsed: 0, duration: 0 }))
    dispatch(audioGuideSlice.actions.setTranscript(null))
    dispatch(audioGuideSlice.actions.setError(null))

    const callbacks = {
      onStatusChange: (status: AudioGuidePlaybackStatus) => {
        if (isStale()) return
        dispatch(audioGuideSlice.actions.setStatus(status))
      },
      onProgress: (payload: { elapsed: number; duration: number }) => {
        if (isStale()) return
        dispatch(audioGuideSlice.actions.setProgress(payload))
      },
      onTranscript: (text: string) => {
        if (isStale()) return
        dispatch(audioGuideSlice.actions.setTranscript(text))
      },
      onError: (message: string) => {
        if (isStale()) return
        dispatch(audioGuideSlice.actions.setError(message))
        dispatch(audioGuideSlice.actions.setStatus("error"))
      },
      onEnded: () => {
        if (isStale()) return
        const next = getNextScript(script)
        if (!next) {
          releaseAudioSession()
          dispatch(audioGuideSlice.actions.reset())
        } else {
          void dispatch(startAudioGuide({ place, script: next }))
        }
      },
    }

    openAiPlaybackEngine.stop()

    if (isStale()) return

    if (openAiPlaybackEngine.hasBufferedAudio(key)) {
      await openAiPlaybackEngine.playBufferedAudio(key, callbacks)
      return
    }

    try {
      const response = await getVoiceApi().createAudioGuide({
        entity_ref: place.entity_ref,
        script,
      })
      if (isStale()) return

      await openAiPlaybackEngine.start(response, key, callbacks)
    } catch (error) {
      if (isStale()) return

      if (error instanceof ClientRateLimitException) {
        const reason = error.data.reason
        setAnalyticsUserProperties({
          type: "rateLimitProps",
          rateLimit: { reason },
        })
        releaseAudioSession()
        dispatch(audioGuideSlice.actions.reset())

        if (
          reason === "anon_throttling" ||
          reason === "unverified_throttling"
        ) {
          void dispatch(showRegisterGateDialog("listen"))
        } else {
          dispatch(
            audioGuideSlice.actions.setError(
              "Too many requests. Please try again later."
            )
          )
          dispatch(audioGuideSlice.actions.setStatus("error"))
        }
        return
      }

      const message =
        error instanceof Error ? error.message : "Unable to load audio guide."
      dispatch(audioGuideSlice.actions.setError(message))
      dispatch(audioGuideSlice.actions.setStatus("error"))
    }
  }
)

export const pauseAudioGuide = createAsyncThunk(
  "audioGuide/pause",
  async () => {
    openAiPlaybackEngine.pause()
  }
)

export const resumeAudioGuide = createAsyncThunk(
  "audioGuide/resume",
  async () => {
    openAiPlaybackEngine.resume()
  }
)

export const stopAudioGuide = createAsyncThunk(
  "audioGuide/stop",
  async (_, thunkApi) => {
    sessionCounter++
    openAiPlaybackEngine.stop()
    currentPlace = null
    releaseAudioSession()
    thunkApi.dispatch(audioGuideSlice.actions.reset())
  }
)

export const switchAudioGuideScript = createAsyncThunk<
  void,
  { script?: AudioGuideScript },
  { state: RootState }
>("audioGuide/switchScript", async ({ script: targetScript }, thunkApi) => {
  const state = thunkApi.getState()
  const place = currentPlace
  const activeScript = state.audioGuideSlice.activeScript
  if (!place) return
  const script = targetScript ?? getNextScript(activeScript)
  if (!script) return
  await thunkApi.dispatch(startAudioGuide({ place, script }))
})

export function getAudioGuideCurrentPlace(): Place | null {
  return currentPlace
}

const audioGuideSlice = createSlice({
  name: "audioGuide",
  initialState,
  reducers: {
    setSession: (
      state,
      action: PayloadAction<{
        place: AudioGuidePlace
        script: AudioGuideScript
      }>
    ) => {
      state.place = action.payload.place
      state.activeScript = action.payload.script
    },
    setStatus: (state, action: PayloadAction<AudioGuidePlaybackStatus>) => {
      state.status = action.payload
    },
    setProgress: (
      state,
      action: PayloadAction<{ elapsed: number; duration: number }>
    ) => {
      state.elapsed = action.payload.elapsed
      state.duration = action.payload.duration
    },
    setTranscript: (state, action: PayloadAction<string | null>) => {
      state.transcript = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    reset: (state) => {
      state.status = "idle"
      state.error = null
      state.transcript = null
    },
  },
})

export const selectAudioGuideState = (state: RootState) => state.audioGuideSlice

export default audioGuideSlice
